import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import type { Job } from '../../types/api'
import { JOB_STATUSES, JOB_TYPES } from '../../types/api'
import Button from '../../components/ui/Button'
import Badge from '../../components/ui/Badge'
import Modal from '../../components/ui/Modal'
import MarkdownViewer from '../../components/ui/MarkdownViewer'
import { JobStatusBadge, JobTypeBadge } from '../Jobs'
import JobChat from '../../components/ui/JobChat'

function parseFrontmatterArray(md: string, key: string): string[] {
  const match = md.match(/^---\n([\s\S]*?)\n---/)
  if (!match) return []
  const lines = match[1].split('\n')
  let inKey = false
  const items: string[] = []
  for (const line of lines) {
    if (line.trim().startsWith(`${key}:`)) { inKey = true; continue }
    if (inKey) {
      if (/^\s+-\s/.test(line)) {
        items.push(line.replace(/^\s+-\s*/, '').trim().replace(/^"|"$/g, ''))
      } else if (line.trim() !== '') {
        break
      }
    }
  }
  return items
}

function parseFrontmatterValue(md: string, key: string): string {
  const match = md.match(new RegExp(`^${key}:\\s*"?([^"\\n]+)"?`, 'm'))
  return match ? match[1].trim() : ''
}

type EditForm = {
  title: string
  employer: string
  location: string
  employment_type: string
  job_duration: string
  working_languages: string
  salary_range: string
  positions: string
  application_deadline: string
  source_url: string
  source_id: string
  required_topics: string
  preferred_topics: string
  status: string
  type: string
}

export default function HeadhunterJobDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [job, setJob] = useState<Job | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [latestReport, setLatestReport] = useState<{
    created_at: string
    matchCount: number
    topMatch?: string
  } | null>(null)
  const [matchLoading, setMatchLoading] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [editForm, setEditForm] = useState<EditForm | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const loadJob = () => {
    if (!id) return
    api.jobs.get(id)
      .then((j) => setJob(j.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }

  const checkReportExists = () => {
    if (!id) return
    api.jobs.matches.getLatest(id)
      .then((res) => {
        setLatestReport({
          created_at: res.data.created_at,
          matchCount: res.data.matches.length,
          topMatch: res.data.matches[0]?.name,
        })
      })
      .catch(() => setLatestReport(null))
  }

  useEffect(() => { loadJob() }, [id])
  useEffect(() => { checkReportExists() }, [id])

  const openEdit = () => {
    if (!job) return
    setEditForm({
      title: parseFrontmatterValue(job.job, 'title'),
      employer: parseFrontmatterValue(job.job, 'employer'),
      location: parseFrontmatterValue(job.job, 'location'),
      employment_type: parseFrontmatterValue(job.job, 'employment_type'),
      job_duration: parseFrontmatterValue(job.job, 'job_duration'),
      working_languages: parseFrontmatterArray(job.job, 'working_languages').join(', '),
      salary_range: parseFrontmatterValue(job.job, 'salary_range'),
      positions: parseFrontmatterValue(job.job, 'positions'),
      application_deadline: parseFrontmatterValue(job.job, 'application_deadline'),
      source_url: parseFrontmatterValue(job.job, 'source_url'),
      source_id: parseFrontmatterValue(job.job, 'source_id'),
      required_topics: parseFrontmatterArray(job.job, 'required_topics').join(', '),
      preferred_topics: parseFrontmatterArray(job.job, 'preferred_topics').join(', '),
      status: parseFrontmatterValue(job.job, 'status'),
      type: parseFrontmatterValue(job.job, 'type') || 'Exclusive',
    })
    setSaveError(null)
    setShowEdit(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !editForm) return
    setSaveError(null)
    setSaving(true)
    try {
      const required_topics = editForm.required_topics.split(',').map((s) => s.trim()).filter(Boolean)
      const preferred_topics = editForm.preferred_topics.split(',').map((s) => s.trim()).filter(Boolean)
      const working_languages = editForm.working_languages.split(',').map((s) => s.trim()).filter(Boolean)
      const positions = editForm.positions ? parseInt(editForm.positions, 10) : undefined
      await api.jobs.updateFrontmatter(id, {
        title: editForm.title,
        employer: editForm.employer,
        ...(editForm.location && { location: editForm.location }),
        ...(editForm.employment_type && { employment_type: editForm.employment_type }),
        ...(editForm.job_duration && { job_duration: editForm.job_duration }),
        ...(working_languages.length && { working_languages }),
        ...(editForm.salary_range && { salary_range: editForm.salary_range }),
        ...(positions && { positions }),
        ...(editForm.application_deadline && { application_deadline: editForm.application_deadline }),
        ...(editForm.source_url && { source_url: editForm.source_url }),
        ...(editForm.source_id && { source_id: editForm.source_id }),
        ...(required_topics.length && { required_topics }),
        ...(preferred_topics.length && { preferred_topics }),
        ...(editForm.status && { status: editForm.status }),
        ...(editForm.type && { type: editForm.type }),
      })
      setShowEdit(false)
      setLoading(true)
      loadJob()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const runMatches = async () => {
    if (!id) return
    setMatchLoading(true)
    try {
      const res = await api.jobs.matches.create(id)
      setLatestReport({
        created_at: res.data.created_at,
        matchCount: res.data.matches.length,
        topMatch: res.data.matches[0]?.name,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Matching failed')
    } finally {
      setMatchLoading(false)
    }
  }

  const isMatchRecentlyRun = (): boolean => {
    if (!latestReport) return false
    const oneHourAgo = Date.now() - 60 * 60 * 1000
    return new Date(latestReport.created_at).getTime() > oneHourAgo
  }

  const timeUntilNextRun = (): string => {
    if (!latestReport) return ''
    const lastRun = new Date(latestReport.created_at).getTime()
    const nextRun = lastRun + 60 * 60 * 1000
    const minutesLeft = Math.ceil((nextRun - Date.now()) / (60 * 1000))
    return `${minutesLeft} min`
  }

  if (loading) return <p className="text-text-muted text-sm">Loading job...</p>
  if (error) return <p className="text-red-400 text-sm">{error}</p>
  if (!job) return <p className="text-text-muted text-sm">Not found.</p>

  const title = parseFrontmatterValue(job.job, 'title')
  const employer = parseFrontmatterValue(job.job, 'employer')
  const location = parseFrontmatterValue(job.job, 'location')
  const employment_type = parseFrontmatterValue(job.job, 'employment_type')
  const status = parseFrontmatterValue(job.job, 'status')
  const type = parseFrontmatterValue(job.job, 'type') || 'Exclusive'
  const requiredTopics = parseFrontmatterArray(job.job, 'required_topics')
  const preferredTopics = parseFrontmatterArray(job.job, 'preferred_topics')
  const job_duration = parseFrontmatterValue(job.job, 'job_duration')
  const working_languages = parseFrontmatterArray(job.job, 'working_languages')
  const salary_range = parseFrontmatterValue(job.job, 'salary_range')
  const positions = parseFrontmatterValue(job.job, 'positions')
  const application_deadline = parseFrontmatterValue(job.job, 'application_deadline')

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/headhunter/jobs')}
            className="text-xs text-text-muted hover:text-primary tracking-tactical"
          >
            ← JOBS
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-3xl">{title || 'Unknown'}</h1>
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-text-muted">
                  {employer && <span className="font-medium text-foreground">{employer}</span>}
                  {location && <span>{location}</span>}
                  {employment_type && <span>{employment_type}</span>}
                  {type && <JobTypeBadge type={type} />}
                  {status && <JobStatusBadge status={status} />}
                </div>
              </div>
              <Button variant="ghost" onClick={openEdit}>EDIT</Button>
            </div>

            {(job_duration || working_languages?.length || salary_range || positions || application_deadline) && (
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-text-muted">
                {job_duration && <span><strong className="text-foreground">Duration:</strong> {job_duration}</span>}
                {working_languages?.length && <span><strong className="text-foreground">Languages:</strong> {working_languages.join(', ')}</span>}
                {salary_range && <span><strong className="text-foreground">Salary:</strong> {salary_range}</span>}
                {positions && <span><strong className="text-foreground">Positions:</strong> {positions}</span>}
                {application_deadline && <span><strong className="text-foreground">Deadline:</strong> {application_deadline}</span>}
              </div>
            )}

            {requiredTopics.length > 0 && (
              <div>
                <p className="text-xs text-text-muted tracking-tactical mb-2">REQUIRED</p>
                <div className="flex flex-wrap gap-1">
                  {requiredTopics.map((t) => <Badge key={t} variant="required">{t}</Badge>)}
                </div>
              </div>
            )}

            {preferredTopics.length > 0 && (
              <div>
                <p className="text-xs text-text-muted tracking-tactical mb-2">PREFERRED</p>
                <div className="flex flex-wrap gap-1">
                  {preferredTopics.map((t) => <Badge key={t} variant="preferred">{t}</Badge>)}
                </div>
              </div>
            )}

            <div className="border-t border-border-subtle pt-4">
              <MarkdownViewer content={job.job} />
            </div>
          </div>

          <div className="space-y-4">
            {latestReport ? (
              <div className="bg-surface-low border border-border-subtle p-4 space-y-3">
                <p className="text-xs text-text-muted tracking-tactical">LATEST MATCH REPORT</p>
                <p className="text-sm font-medium text-foreground">
                  {latestReport.matchCount} candidate{latestReport.matchCount !== 1 ? 's' : ''} found
                </p>
                <p className="text-xs text-text-muted">
                  {new Date(latestReport.created_at).toLocaleString()}
                </p>
                <div className="flex flex-col gap-2 pt-3 border-t border-border-subtle">
                  <Button
                    variant="primary"
                    onClick={runMatches}
                    disabled={matchLoading || isMatchRecentlyRun()}
                    className="w-full justify-center"
                  >
                    {matchLoading ? 'CREATING...' : isMatchRecentlyRun() ? `RUN AGAIN (${timeUntilNextRun()})` : 'RUN MATCHES'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate(`/match-reports/${id}`)}
                    className="w-full justify-center text-sm"
                  >
                    VIEW FULL REPORT
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-surface-low border border-border-subtle p-4 space-y-3">
                <p className="text-xs text-text-muted tracking-tactical">MATCHING</p>
                <p className="text-sm text-text-muted">No match reports yet.</p>
                <Button
                  variant="primary"
                  onClick={runMatches}
                  disabled={matchLoading}
                  className="w-full justify-center"
                >
                  {matchLoading ? 'CREATING...' : 'RUN MATCHES'}
                </Button>
              </div>
            )}

            <div>
              <p className="text-xs text-text-muted tracking-tactical mb-2">JOB CHAT</p>
              <JobChat jobId={id!} onJobUpdated={loadJob} />
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={showEdit} onClose={() => setShowEdit(false)} title="EDIT JOB">
        {editForm && (
          <form onSubmit={handleSave} className="space-y-3">
            {saveError && <p className="text-red-400 text-xs">{saveError}</p>}
            {[
              { key: 'title', label: 'TITLE *', placeholder: 'Senior Engineer' },
              { key: 'employer', label: 'EMPLOYER *', placeholder: 'Acme Corp' },
              { key: 'location', label: 'LOCATION', placeholder: 'Helsinki, Finland' },
              { key: 'employment_type', label: 'EMPLOYMENT TYPE', placeholder: 'Full-time' },
              { key: 'job_duration', label: 'DURATION', placeholder: '3-6 months / permanent' },
              { key: 'working_languages', label: 'WORKING LANGUAGES (comma-separated)', placeholder: 'Finnish, English' },
              { key: 'salary_range', label: 'SALARY RANGE', placeholder: '4000-5000 €/month' },
              { key: 'positions', label: 'POSITIONS', placeholder: '1', type: 'number' },
              { key: 'application_deadline', label: 'APPLICATION DEADLINE', placeholder: 'YYYY-MM-DD' },
              { key: 'source_url', label: 'SOURCE URL', placeholder: 'https://example.com/job' },
              { key: 'source_id', label: 'SOURCE ID', placeholder: 'External job ID' },
              { key: 'required_topics', label: 'REQUIRED TOPICS (comma-separated)', placeholder: 'TypeScript, React, Node.js' },
              { key: 'preferred_topics', label: 'PREFERRED TOPICS (comma-separated)', placeholder: 'Docker, AWS' },
            ].map(({ key, label, placeholder, type = 'text' }) => (
              <div key={key}>
                <label className="block text-xs text-text-muted tracking-tactical mb-1">{label}</label>
                <input
                  type={type}
                  value={editForm[key as keyof EditForm]}
                  onChange={(e) => setEditForm((f) => f ? { ...f, [key]: e.target.value } : f)}
                  placeholder={placeholder}
                  className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
                />
              </div>
            ))}
            <div>
              <label className="block text-xs text-text-muted tracking-tactical mb-1">STATUS</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm((f) => f ? { ...f, status: e.target.value } : f)}
                className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
              >
                {JOB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-muted tracking-tactical mb-1">TYPE</label>
              <select
                value={editForm.type}
                onChange={(e) => setEditForm((f) => f ? { ...f, type: e.target.value } : f)}
                className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
              >
                {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving}>{saving ? 'SAVING...' : 'SAVE CHANGES'}</Button>
              <Button variant="ghost" type="button" onClick={() => setShowEdit(false)}>CANCEL</Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  )
}
